import { useEffect, useRef, useState } from 'react';
import type { ModelWithStats, Place, PoseRef, Scene } from '@studio/shared';

import { api, assetUrl, sceneSentUrl, setCatalogueMode, type Presets } from '../lib/api';
import { useErrorText } from '../lib/errors';
import { usePresetLabel, useT } from '../lib/i18n';
import { useExamples } from '../lib/useExamples';
import { useLoad } from '../lib/useLoad';
import { addPosePhoto } from '../lib/poses';
import { downscaleImage } from '../lib/downscale';
import { ModelEditor } from '../pages/Models';
import { useDialog } from './Dialog';
import { ImageSource } from './ImageSource';
import { PencilIcon, PlusIcon, TrashIcon } from './Icons';
import { OptionCard } from './OptionCard';
import { useToast } from './Toaster';

type Shelf = 'models' | 'places' | 'poses' | 'scenes';

/**
 * The Studio catalogue: the models, places, poses and scenes every client is offered.
 *
 * Managed here and only here. While this is open, the requests that make
 * models, places, poses and photographs are carried out as the catalogue, so
 * creating a model is the same editor clients use — portrait, turnaround and
 * all — and what it makes lands in the catalogue, not in the staff account.
 */
export function Catalogue() {
  const t = useT();
  const [shelf, setShelf] = useState<Shelf>('models');

  // Set during render, so the first loads below already go to the catalogue.
  useState(() => {
    setCatalogueMode(true);
    return true;
  });
  useEffect(() => {
    setCatalogueMode(true);
    return () => setCatalogueMode(false);
  }, []);

  const models = useLoad(() => api.catalogue.models(), { initial: [] as ModelWithStats[] });
  const places = useLoad(() => api.catalogue.places(), { initial: [] as Place[] });
  const poses = useLoad(() => api.catalogue.poses(), { initial: [] as PoseRef[] });
  const scenes = useLoad(() => api.catalogue.scenes(), { initial: [] as Scene[] });
  /* The Studio's built-in sets, offered beside the catalogue's own places in every roll. */
  const presets = useLoad(() => api.presets(), { initial: undefined as Presets | undefined });
  const builtins = presets.data?.backdrops ?? [];

  const counts: Record<Shelf, number> = {
    models: models.data.length,
    places: places.data.length + builtins.length,
    poses: poses.data.length,
    scenes: scenes.data.length,
  };

  return (
    <div className="stack stack--lg">
      <p className="muted">{t('cat.lede')}</p>
      <div className="chips" role="tablist" aria-label={t('admin.tab.catalogue')}>
        {(['models', 'places', 'poses', 'scenes'] as const).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={shelf === id}
            className={`chip ${shelf === id ? 'is-on' : ''}`}
            onClick={() => setShelf(id)}
          >
            {t(`cat.shelf.${id}`)} <b>{counts[id]}</b>
          </button>
        ))}
      </div>

      {shelf === 'models' && <Models models={models.data} reload={models.reload} />}
      {shelf === 'places' && <Places places={places.data} scenes={builtins} reload={places.reload} />}
      {shelf === 'poses' && <Poses poses={poses.data} reload={poses.reload} />}
      {shelf === 'scenes' && <Scenes scenes={scenes.data} reload={scenes.reload} />}
    </div>
  );
}

/* ------------------------------ models ------------------------------ */

function Models({ models, reload }: { models: ModelWithStats[]; reload: () => void }) {
  const t = useT();
  const toast = useToast();
  const [editing, setEditing] = useState<ModelWithStats | 'new' | undefined>();

  return (
    <>
      <div className="wiz__grid">
        <button type="button" className="wiz__add" onClick={() => setEditing('new')}>
          <PlusIcon />
          <span>{t('cat.addModel')}</span>
        </button>
        {models.map((model) => {
          const portrait =
            model.referenceAssetIds[0] ?? model.views?.find((v) => v.kind === 'front')?.assetId;
          return (
            <OptionCard
              key={model.id}
              title={model.name}
              spec={t('cat.edit')}
              selected={false}
              image={portrait ? assetUrl(portrait) : undefined}
              fallback={<span className="wiz__initial">{model.name.charAt(0)}</span>}
              onToggle={() => setEditing(model)}
            />
          );
        })}
      </div>
      {models.length === 0 && <p className="faint">{t('cat.emptyModels')}</p>}

      {editing && (
        <ModelEditor
          model={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={(message) => {
            setEditing(undefined);
            reload();
            toast(message, 'ok');
          }}
          onChanged={reload}
          toast={toast}
          t={t}
          manual
        />
      )}
    </>
  );
}

/* ------------------------------ places ------------------------------ */

function Places({
  places,
  scenes,
  reload,
}: {
  places: Place[];
  scenes: Presets['backdrops'];
  reload: () => void;
}) {
  const t = useT();
  const tp = usePresetLabel();
  const examples = useExamples();
  /** The built-in sets are many; narrowed by kind, as the roll's place step does. */
  const [kind, setKind] = useState<'all' | 'street' | 'studio'>('all');
  const toast = useToast();
  const describe = useErrorText();
  const { ask } = useDialog();
  /** The place being edited, or 'new'; nothing when the list is showing. */
  const [form, setForm] = useState<Place | 'new' | undefined>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetId, setAssetId] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const open = (place: Place | 'new') => {
    setForm(place);
    setName(place === 'new' ? '' : place.name);
    setDescription(place === 'new' ? '' : place.description);
    setAssetId(place === 'new' ? undefined : place.assetId);
    setError('');
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (form === 'new') {
        await api.createPlace({ name: name.trim(), description: description.trim(), assetId });
      } else if (form) {
        await api.updatePlace(form.id, {
          name: name.trim(),
          description: description.trim(),
          assetId: assetId ?? null,
        });
      }
      setForm(undefined);
      reload();
      toast(t('cat.saved'), 'ok');
    } catch (err) {
      setError(describe(err, 'wiz.placeFailed'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (place: Place) => {
    if (
      !(await ask({
        title: t('wiz.removePlace', { name: place.name }),
        body: t('cat.removeBody'),
        confirmLabel: t('wiz.removeGo'),
        danger: true,
      }))
    )
      return;
    await api.deletePlace(place.id).catch((err) => toast(describe(err, 'cat.failed'), 'error'));
    setForm(undefined);
    reload();
  };

  if (form) {
    return (
      <div className="wiz__form">
        <div className="wiz__formPhoto">
          <ImageSource
            kind="backdrop"
            assetId={assetId}
            onChange={(asset) => setAssetId(asset?.id)}
            prompt={t('wiz.placePhoto')}
            ratio="4 / 3"
          />
        </div>
        <div className="wiz__formFields">
          <div className="field">
            <label htmlFor="cat-place-name">{t('wiz.placeName')}</label>
            <input
              id="cat-place-name"
              className="input"
              value={name}
              placeholder={t('wiz.placeNamePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cat-place-desc">{t('wiz.placeDesc')}</label>
            <textarea
              id="cat-place-desc"
              className="textarea"
              rows={4}
              value={description}
              placeholder={t('wiz.placeDescPlaceholder')}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {error && <div className="banner banner--error">{error}</div>}
          <div className="row row--wrap">
            <button type="button" className="btn btn--ghost" onClick={() => setForm(undefined)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !name.trim() || (!description.trim() && !assetId)}
              onClick={() => void save()}
            >
              {busy ? <span className="spinner" /> : null}
              {t(form === 'new' ? 'wiz.placeSave' : 'common.save')}
            </button>
            {form !== 'new' && (
              <button type="button" className="btn btn--danger" onClick={() => void remove(form)}>
                <TrashIcon /> {t('wiz.removeGo')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="wiz__grid">
        <button type="button" className="wiz__add" onClick={() => open('new')}>
          <PlusIcon />
          <span>{t('wiz.addPlace')}</span>
        </button>
        {places.map((place) => (
          <OptionCard
            key={place.id}
            title={place.name}
            spec={t('cat.edit')}
            description={place.description}
            selected={false}
            image={place.assetId ? assetUrl(place.assetId) : undefined}
            fallback={<span className="wiz__initial">{place.name.charAt(0)}</span>}
            onToggle={() => open(place)}
          />
        ))}
      </div>
      <p className="faint">{t(places.length ? 'cat.placesHint' : 'cat.emptyPlaces')}</p>

      {scenes.length > 0 && (
        <section className="stack" aria-labelledby="cat-builtin">
          <div className="row row--wrap row--between">
            <h3 id="cat-builtin" className="cat__subhead">
              {t('cat.builtinPlaces')} <b>{scenes.length}</b>
            </h3>
            <div className="chips">
              {(['all', 'street', 'studio'] as const).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`chip chip--quiet ${kind === id ? 'is-on' : ''}`}
                  aria-pressed={kind === id}
                  onClick={() => setKind(id)}
                >
                  {t(`wiz.kind.${id}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="wiz__grid">
            {scenes
              .filter((scene) =>
                kind === 'all'
                  ? true
                  : kind === 'street'
                    ? scene.kind === 'location'
                    : scene.kind === 'studio',
              )
              .map((scene) => (
                <OptionCard
                  key={scene.id}
                  title={tp(`scene.${scene.id}.name`, scene.name)}
                  spec={t('cat.builtin')}
                  selected={false}
                  example={examples[`scene-${scene.id}`]}
                  swatch={scene.swatch}
                  onToggle={() => undefined}
                />
              ))}
          </div>
          <p className="faint">{t('cat.builtinHint')}</p>
        </section>
      )}
    </>
  );
}

/* ------------------------------- poses ------------------------------ */

function Poses({ poses, reload }: { poses: PoseRef[]; reload: () => void }) {
  const t = useT();
  const toast = useToast();
  const describe = useErrorText();
  const { ask, prompt } = useDialog();
  const [adding, setAdding] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  /* Each photograph is a pose as soon as it is in: nothing to fill in, nothing read. */
  const add = async (files: FileList | null) => {
    const list = Array.from(files ?? []).slice(0, 24);
    if (fileRef.current) fileRef.current.value = '';
    if (!list.length) return;
    setAdding(list.length);
    for (const file of list) {
      try {
        await addPosePhoto(file);
        reload();
      } catch (err) {
        toast(describe(err, 'wiz.poseFailed'), 'error');
      }
      setAdding((n) => n - 1);
    }
  };

  const rename = async (pose: PoseRef) => {
    const next = await prompt({
      title: t('wiz.rename'),
      label: t('wiz.placeName'),
      value: pose.name,
      confirmLabel: t('common.save'),
    });
    if (!next?.trim() || next.trim() === pose.name) return;
    await api
      .renamePose(pose.id, next.trim())
      .then(reload)
      .catch((err) => toast(describe(err, 'wiz.renameFailed'), 'error'));
  };

  const remove = async (pose: PoseRef) => {
    if (
      !(await ask({
        title: t('wiz.removePose', { name: pose.name }),
        body: t('cat.removeBody'),
        confirmLabel: t('wiz.removeGo'),
        danger: true,
      }))
    )
      return;
    await api
      .deletePose(pose.id)
      .then(reload)
      .catch((err) => toast(describe(err, 'cat.failed'), 'error'));
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => void add(event.target.files)}
      />
      <div className="wiz__grid">
        <button
          type="button"
          className="wiz__add"
          disabled={adding > 0}
          onClick={() => fileRef.current?.click()}
        >
          {adding > 0 ? <span className="spinner" /> : <PlusIcon />}
          <span>{adding > 0 ? t('wiz.poseAdding', { n: adding }) : t('wiz.addPose')}</span>
          {adding === 0 && <small>{t('wiz.addPoseHint')}</small>}
        </button>
        {poses.map((pose) => (
          <div key={pose.id} className="wiz__card">
            <OptionCard
              title={pose.name}
              selected={false}
              image={assetUrl(pose.assetId)}
              onToggle={() => void rename(pose)}
            />
            <button
              type="button"
              className="wiz__rename cat__always"
              aria-label={t('wiz.renameNamed', { name: pose.name })}
              title={t('wiz.renameNamed', { name: pose.name })}
              onClick={() => void rename(pose)}
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              className="wiz__remove cat__always"
              aria-label={t('wiz.removePose', { name: pose.name })}
              title={t('wiz.removePose', { name: pose.name })}
              onClick={() => void remove(pose)}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
      <p className="faint">{t(poses.length ? 'cat.posesHint' : 'cat.emptyPoses')}</p>
    </>
  );
}

/* ------------------------------ scenes ------------------------------ */

/**
 * Finished photographs — a place, a pose, an outfit, the Studio's model — that
 * clients remake with their own model and pieces. Each photograph is a scene as
 * soon as it is in; a frame from a roll is offered from its loupe.
 */
function Scenes({ scenes, reload }: { scenes: Scene[]; reload: () => void }) {
  const t = useT();
  const toast = useToast();
  const describe = useErrorText();
  const { ask, prompt } = useDialog();
  const [adding, setAdding] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const add = async (files: FileList | null) => {
    const list = Array.from(files ?? []).slice(0, 24);
    if (fileRef.current) fileRef.current.value = '';
    if (!list.length) return;
    setAdding(list.length);
    for (const file of list) {
      try {
        const asset = await api.uploadAsset(await downscaleImage(file), 'reference');
        await api.createScene({ assetId: asset.id });
        reload();
      } catch (err) {
        toast(describe(err, 'cat.failed'), 'error');
      }
      setAdding((n) => n - 1);
    }
  };

  const rename = async (scene: Scene) => {
    const next = await prompt({
      title: t('wiz.rename'),
      label: t('cat.sceneName'),
      value: scene.name,
      confirmLabel: t('common.save'),
    });
    if (!next?.trim() || next.trim() === scene.name) return;
    await api
      .renameScene(scene.id, next.trim())
      .then(reload)
      .catch((err) => toast(describe(err, 'wiz.renameFailed'), 'error'));
  };

  const remove = async (scene: Scene) => {
    if (
      !(await ask({
        title: t('cat.removeScene', { name: scene.name }),
        body: t('cat.removeBody'),
        confirmLabel: t('wiz.removeGo'),
        danger: true,
      }))
    )
      return;
    await api
      .deleteScene(scene.id)
      .then(reload)
      .catch((err) => toast(describe(err, 'cat.failed'), 'error'));
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => void add(event.target.files)}
      />
      <div className="wiz__grid">
        <button
          type="button"
          className="wiz__add"
          disabled={adding > 0}
          onClick={() => fileRef.current?.click()}
        >
          {adding > 0 ? <span className="spinner" /> : <PlusIcon />}
          <span>{t('cat.addScene')}</span>
          {adding === 0 && <small>{t('cat.addSceneHint')}</small>}
        </button>
        {scenes.map((scene) => (
          <div key={scene.id} className="wiz__card">
            <OptionCard
              title={scene.name}
              selected={false}
              // As Gemini will be sent it: the head of the woman in it blurred.
              image={sceneSentUrl(scene.id)}
              onToggle={() => void rename(scene)}
            />
            <button
              type="button"
              className="wiz__rename cat__always"
              aria-label={t('wiz.renameNamed', { name: scene.name })}
              title={t('wiz.renameNamed', { name: scene.name })}
              onClick={() => void rename(scene)}
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              className="wiz__remove cat__always"
              aria-label={t('cat.removeScene', { name: scene.name })}
              title={t('cat.removeScene', { name: scene.name })}
              onClick={() => void remove(scene)}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
      <p className="faint">{t(scenes.length ? 'cat.scenesHint' : 'cat.emptyScenes')}</p>
    </>
  );
}
